import React from 'react';

const STORAGE_KEY = 'esc-dossier-panel-sizes-v2';
const SAVE_DEBOUNCE_MS = 300;

const DEFAULTS = { left: 55, right: 25 };

interface PanelSizes {
  left: number;
  right: number;
}

export interface DossierPanelState {
  sizes: PanelSizes;
}

export interface DossierPanelHandlers {
  onSplitterResize(sizes: number[]): void;
}

function loadSizes(): PanelSizes {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.left === 'number' && typeof parsed.right === 'number') {
        return { left: parsed.left, right: parsed.right };
      }
    }
  } catch {
    // ignore
  }
  return DEFAULTS;
}

function saveSizes(sizes: PanelSizes): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes));
  } catch {
    // ignore
  }
}

export function useDossierPanelSizes(): [
  DossierPanelState,
  DossierPanelHandlers
] {
  const loaded = React.useMemo(loadSizes, []);
  const [state, setState] = React.useState<DossierPanelState>({
    sizes: loaded,
  });
  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  React.useEffect(
    () => () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    },
    []
  );

  const handlers = React.useMemo<DossierPanelHandlers>(
    () => ({
      onSplitterResize(newSizes: number[]) {
        if (newSizes.length < 2) {
          return;
        }
        setState((s) => {
          const total = newSizes.reduce((a, b) => a + b, 0);
          if (total === 0) {
            return s;
          }
          const toPct = (px: number) => (px / total) * 100;
          const updated = {
            left: toPct(newSizes[0]),
            right: newSizes[2] != null ? toPct(newSizes[2]) : s.sizes.right,
          };
          if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
          }
          saveTimerRef.current = setTimeout(
            () => saveSizes(updated),
            SAVE_DEBOUNCE_MS
          );
          return { sizes: updated };
        });
      },
    }),
    []
  );

  return [state, handlers];
}
